<template>
    <div class="table-wrapper overflow-x-auto" ref="wrapper">
        <table class="table is-bordered is-striped global-table min-w-full" style="width: 100%;">
            <thead ref="thead">
                <tr>
                    <th rowspan="2" class="bg-gray-200 text-center">{{ $t('project') }}</th>
                    <!-- <th rowspan="2" class="bg-gray-200 text-center">{{ $t('status') }}</th> -->
                    <th v-if="allExtraFields.length" :colspan="allExtraFields.length" class="bg-gray-200 text-center">
                        {{ $t('extrafields') }}
                    </th>
                    <th v-for="group in groupsWithTemplates" :key="'group_' + group.id + '_type' + typeId"
                        :colspan="group.templates.length * 2" :class="['text-center', getGroupColor(group.name)]">
                        {{ group.name }}
                    </th>
                    <th rowspan="2" class="bg-gray-200 text-center">{{ $t('issues') }}</th>
                    <th rowspan="2" class="bg-gray-200 text-center">{{ $t('comments') }}</th>
                </tr>
                <tr>
                    <th v-for="(field, index) in allExtraFields" :key="'extrafields_' + index + '_type' + typeId"
                        class="bg-gray-200 text-center">
                        {{ field.name }}
                    </th>
                    <template v-for="group in groupsWithTemplates">
                        <template v-for="tpl in group.templates">
                            <th :key="tpl.id" :class="getGroupColor(group.name)">
                                {{ tpl.name }}
                            </th>
                            <th :key="tpl.id + '_comments'" :class="getGroupColor(group.name)">
                                Comments
                            </th>
                        </template>
                    </template>
                </tr>
            </thead>
            <tbody>
                <tr v-for="project in projects" :key="'project_' + project.id + '_type' + typeId">
                    <td>{{ project.name }}</td>
                    <td v-for="field in allExtraFields" :key="'field_' + field.id + '_type' + typeId">
                        <template v-if="project.editingExtraField === field.id">
                            <input type="text" :value="getExtraFieldValue(project, field.id)" class="input is-small"
                                @input="updateExtraFieldValue(project, field.id, $event.target.value)"
                                @blur="saveExtraField(project, field)" @keyup.enter="saveExtraField(project, field)">
                        </template>
                        <template v-else>
                            <span @click="editExtraField(project, field)" style="cursor:pointer;" class="text-xs">
                                {{ getExtraFieldValue(project, field.id) || '-' }}
                            </span>
                        </template>
                    </td>
                    <template v-for="group in groupsWithTemplates">
                        <template v-for="tpl in group.templates">
                            <!-- Celda de template -->
                            <td :key="'tpl_' + tpl.id + '_type' + typeId" class="text-xs">
                                <template v-if="getPermitting(project, tpl.code)">
                                    <!-- Icono de advertencia -->
                                    <b-tooltip v-if="getPermittingIcon(project, tpl.code).icon"
                                        :label="getPermittingIcon(project, tpl.code).tooltip" position="is-left">
                                        <span :class="getPermittingIcon(project, tpl.code).color">
                                            <i :class="getPermittingIcon(project, tpl.code).icon"></i>
                                        </span>
                                    </b-tooltip>
                                    <!-- Planned Start -->
                                    <b-tooltip :label="$t('planned_start')" position="is-top">
                                        <span :class="['font-semibold', getPermittingColor(project, tpl.code)]">
                                            {{ formatShortDate(getPermitting(project, tpl.code).activity?.current_start_date) }}
                                        </span>
                                    </b-tooltip><br>
                                    <!-- Planned End -->
                                    <b-tooltip :label="$t('planned_end')" position="is-top">
                                        <span :class="['font-semibold', getPermittingColor(project, tpl.code)]">
                                            {{ formatShortDate(getPermitting(project, tpl.code).activity?.current_end_date) }}
                                        </span>
                                    </b-tooltip><br>
                                    <!-- Actual Start -->
                                    <b-tooltip :label="$t('actual_start')" position="is-top">
                                        <span :class="['font-semibold', getPermittingColor(project, tpl.code)]">
                                            {{ formatShortDate(getPermitting(project, tpl.code).activity?.gantt_real_start_date) }}
                                        </span>
                                    </b-tooltip><br>
                                    <!-- Actual End (solo si progreso es 100%) -->
                                    <b-tooltip :label="$t('actual_end')" position="is-top">
                                        <span :class="['font-semibold', getPermittingColor(project, tpl.code)]">
                                            {{
                                                getPermitting(project, tpl.code).activity &&
                                                Number(getPermitting(project, tpl.code).activity.progress_actual) >= 100
                                                    ? formatShortDate(getPermitting(project, tpl.code).activity.gantt_real_end_date)
                                                    : '-'
                                            }}
                                        </span>
                                    </b-tooltip><br>
                                    <!-- Response Date -->
                                    <b-tooltip :label="`${$t('response_date')}: (${
                                        getPermitting(project, tpl.code).status === 0 ? $t('approved') :
                                        getPermitting(project, tpl.code).status === 1 ? $t('approved_with_comments') :
                                        getPermitting(project, tpl.code).status === 2 ? $t('rejected') :
                                        getPermitting(project, tpl.code).status === 3 ? $t('rejected_with_comments') :
                                        '-'
                                    })`" position="is-top">
                                        <span :class="['font-semibold', getPermittingColor(project, tpl.code)]">
                                            {{ formatShortDate(getPermitting(project, tpl.code).response_date) }}
                                        </span>
                                    </b-tooltip><br>
                                    <!-- Expiration Date -->
                                    <b-tooltip :label="$t('expiration_date')" position="is-top">
                                        <span :class="['font-semibold', getPermittingColor(project, tpl.code)]">
                                            {{ formatShortDate(getPermitting(project, tpl.code).expiration_date) }}
                                        </span>
                                    </b-tooltip>
                                </template>
                                <template v-else>-</template>
                            </td>
                            <!-- Celda de comments para el template -->
                            <td :key="'tpl_' + tpl.id + '_comments_type' + typeId" class="comments-cell">
                                <template
                                    v-if="project.editingTemplateComments && project.editingTemplateComments[tpl.code]">
                                    <input type="text" v-model="project.templateComments[tpl.code]"
                                        class="input is-small" @blur="saveTemplateComment(project, tpl)"
                                        @keyup.enter="saveTemplateComment(project, tpl)" ref="tplCommentInput">
                                </template>
                                <template v-else>
                                    <span @click="editTemplateComment(project, tpl)" style="cursor:pointer;"
                                        class="text-xs">
                                        {{ project.templateComments && project.templateComments[tpl.code]
                                            ? project.templateComments[tpl.code]
                                            : (getPermitting(project, tpl.code)?.comments || '-') }}
                                    </span>
                                </template>
                            </td>
                        </template>
                    </template>
                    <!-- Incidents column -->
                    <td>
                        <template v-if="project.editingIncidents">
                            <input type="text" v-model="project.incidents" placeholder="Incidents"
                                class="input is-small" @blur="saveField(project, 'incidents')"
                                @keyup.enter="saveField(project, 'incidents')" ref="incidentsInput">
                        </template>
                        <template v-else>
                            <b-tooltip :label="'Click to edit incidents'" position="is-top">
                                <span @click="editField(project, 'incidents')" style="cursor:pointer;" class="text-xs">
                                    {{ project.incidents && project.incidents.trim() !== '' ? project.incidents : '-' }}
                                </span>
                            </b-tooltip>
                        </template>
                    </td>
                    <!-- Comments column -->
                    <td class="comments-cell">
                        <template v-if="project.editingComments">
                            <input type="text" v-model="project.comments" placeholder="Comments" class="input is-small"
                                @blur="saveField(project, 'comments')" @keyup.enter="saveField(project, 'comments')"
                                ref="commentsInput">
                        </template>
                        <template v-else>
                            <b-tooltip :label="'Click to edit comments'" position="is-top">
                                <span @click="editField(project, 'comments')" style="cursor:pointer;" class="text-xs">
                                    {{ project.comments && project.comments.trim() !== '' ? project.comments : '-' }}
                                </span>
                            </b-tooltip>
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment';
import axios from 'axios'

export default {
    props: {
        // groups: Array,
        // projects: Array,
        typeId: [Number, String],
    },
    data() {
        return {
            colorClasses: [
                'bg-green-lighter',
                'bg-blue-lighter',
                'bg-orange-lighter',
                'bg-yellow-lighter',
                'bg-purple-lighter',
                'bg-red-lighter',
                'bg-teal-lighter',
                'bg-pink-lighter',
                'bg-indigo-lighter',
                'bg-gray-lighter',
            ],
            groupColorMap: {},
            usedColors: [],
            canEdit: true,

            groups: [],
            projects: [],
        }
    },

    computed: {
      ...mapGetters({
            company: 'app/company',
        }),
        groupsWithTemplates() {
            this.assignUniqueColorsToGroups();
            return this.groups.filter(g => g.templates && g.templates.length > 0);
        },
        allExtraFields() {
            const fields = [];
            this.projects.forEach(project => {
                (project.extra_fields || []).forEach(field => {
                    if (!fields.find(f => f.id === field.id)) {
                        fields.push({ id: field.id, name: field.name });
                    }
                });
            });
            return fields;
        },
    },

    created() {
      this.fetchData();
    },

    methods: {
        async fetchData() {
            try {
                const { data } = await axios.get(`/api/v2/company/${this.company.id}/permitting-groups-projects`, {
                    params: { permitting_type_id: this.typeId }
                })

                this.groups = data.data[0].groups || [];
                this.projects = data.data[0].projects || [];
                this.assignUniqueColorsToGroups();
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        },

        assignUniqueColorsToGroups() {
            if (Object.keys(this.groupColorMap).length === this.groups.length) return;
            this.usedColors = [];
            this.groups.forEach(group => {
                if (!this.groupColorMap[group.name]) {
                    const color = this.colorClasses.find(c => !this.usedColors.includes(c));
                    this.groupColorMap[group.name] = color || this.colorClasses[0];
                    this.usedColors.push(this.groupColorMap[group.name]);
                }
            });
        },
        getGroupColor(name) {
            return this.groupColorMap[name] || '';
        },
        formatShortDate(dateStr) {
            if (!dateStr) return '-'
            const date = moment(dateStr)
            return date.isValid() ? date.format('DD/MM/YYYY') : '-'
        },
        getPermitting(project, code) {
            if (!project.permitting_templates) return null;
            const foundTemplate = project.permitting_templates.find(t => t.code === code);
            if (!foundTemplate || !foundTemplate.permittings || foundTemplate.permittings.length === 0) return null;
            // Buscar el permitting cuya actividad sea del proyecto actual
            return foundTemplate.permittings.find(p => p.activity && p.activity.project_id === project.id) || null;
        },
        getPermittingColor(project, code) {
            const permitting = this.getPermitting(project, code);
            if (!permitting) return 'text-black';
            let colorClass = 'text-black';
            if (
                permitting.activity &&
                permitting.activity.current_start_date &&
                permitting.activity.current_end_date
            ) {
                const start = moment(permitting.activity.current_start_date);
                const end = moment(permitting.activity.current_end_date);
                const obtention = moment(permitting.obtention_date);
                if (start.isValid() && end.isValid() && obtention.isValid()) {
                    if (obtention.isBetween(start, end, 'day', '[]')) {
                        colorClass = 'text-blue';
                    }
                    // else {
                    //     colorClass = 'text-red';
                    // }
                }
            }
            return colorClass;
        },
        getPermittingIcon(project, code) {
            const permitting = this.getPermitting(project, code);
            if (!permitting || !permitting.expiration_date) {
                return { icon: '', color: '', tooltip: '' };
            }
            const expiration = moment(permitting.expiration_date, 'YYYY-MM-DD');
            if (!expiration.isValid()) {
                return { icon: '', color: '', tooltip: '' };
            }
            const diffDays = expiration.diff(moment(), 'days');
            if (diffDays < 0) {
                return {
                    icon: "fas fa-exclamation-triangle",
                    color: "text-red",
                    tooltip: this.$t('expired_permitting')
                };
            } else if (diffDays <= 30) {
                return {
                    icon: "fas fa-exclamation-triangle",
                    color: "text-orange",
                    tooltip: this.$t('expiring_soon_permitting')
                };
            }
            return { icon: '', color: '', tooltip: '' };
        },
        editField(project, field) {
            if (!this.canEdit) return;
            this.$set(project, `editing${this.capitalize(field)}`, true);
            this.$nextTick(() => {
                const refName = field + 'Input';
                if (this.$refs[refName] && this.$refs[refName].length) {
                    this.$refs[refName][0].focus();
                } else if (this.$refs[refName]) {
                    this.$refs[refName].focus();
                }
            });
        },
        saveField(project, field) {
            this.$set(project, `editing${this.capitalize(field)}`, false);
            axios.put(`/api/v2/project/${project.id}/notes`, {
                [field]: project[field]
            }).then(() => {
                this.$buefy.toast.open({
                    message: this.$t('save_successful'),
                    type: 'is-success',
                    duration: 2000
                });
            }).catch(() => {
                this.$buefy.toast.open({
                    message: this.$t('error_saving'),
                    type: 'is-danger',
                    duration: 2000
                });
            });
        },
        capitalize(str) {
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
        getExtraFieldValue(project, fieldId) {
            let field = (project.extra_fields || []).find(f => f.id === fieldId);
            return field ? field.value : '';
        },
        updateExtraFieldValue(project, fieldId, value) {
            let field = (project.extra_fields || []).find(f => f.id === fieldId);
            if (!field) {
                field = { id: fieldId, value: '' };
                project.extra_fields.push(field);
            }
            field.value = value;
        },
        editExtraField(project, field) {
            this.$set(project, 'editingExtraField', field.id);
        },
        async saveExtraField(project, field) {
            this.$set(project, 'editingExtraField', null);
            const value = this.getExtraFieldValue(project, field.id);
            try {
                await axios.put(`/api/v2/project/${project.id}/extra-field/${field.id}`, {
                    value
                });
                this.$buefy.toast.open({
                    message: this.$t('save_successful'),
                    type: 'is-success'
                });
            } catch {
                this.$buefy.toast.open({
                    message: this.$t('error_saving'),
                    type: 'is-danger'
                });
            }
        },
        editTemplateComment(project, tpl) {
            if (!project.editingTemplateComments) this.$set(project, 'editingTemplateComments', {});
            if (!project.templateComments) this.$set(project, 'templateComments', {});
            this.$set(project.editingTemplateComments, tpl.code, true);
            // Inicializa el valor si no existe
            if (project.templateComments[tpl.code] === undefined) {
                this.$set(project.templateComments, tpl.code, this.getPermitting(project, tpl.code)?.comments || '');
            }
            this.$nextTick(() => {
                if (this.$refs.tplCommentInput && this.$refs.tplCommentInput.focus) {
                    this.$refs.tplCommentInput.focus();
                }
            });
        },
        async saveTemplateComment(project, tpl) {
            this.$set(project.editingTemplateComments, tpl.code, false);
            const permitting = this.getPermitting(project, tpl.code);
            if (!permitting) {
                this.$buefy.toast.open({
                    message: this.$t('error_saving'),
                    type: 'is-danger'
                });
                return;
            }
            // Fallbacks para campos requeridos
            const permittingTemplateId = permitting.permitting_template_id || tpl.id;
            const activityId = permitting.activity_id || (permitting.activity && permitting.activity.id);
            if (!permittingTemplateId || !activityId) {
                this.$buefy.toast.open({
                    message: this.$t('error_saving'),
                    type: 'is-danger'
                });
                return;
            }
            try {
                await axios.put(`/api/v2/project/${project.id}/permitting/${permitting.id}`, {
                    id: permitting.id,
                    permitting_template_id: permittingTemplateId,
                    activity_id: activityId,
                    comments: project.templateComments[tpl.code]
                });
                this.$buefy.toast.open({
                    message: this.$t('save_successful'),
                    type: 'is-success'
                });
            } catch {
                this.$buefy.toast.open({
                    message: this.$t('error_saving'),
                    type: 'is-danger'
                });
            }
        },
    },
}
</script>
<style lang="scss" scoped>
.table-wrapper{
  max-height: 68vh;
  overflow: auto;
  background:#fff;
  isolation:isolate;
  --thead-row1-h: 80px;
  --th-border: 1px;
  --first-col-w: 260px;
  --first-col-bg: #e5e7eb;
  --first-col-text: #111;
  --thead-row2-first-bg: #fff;
}

::v-deep .global-table{
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #000 !important;
}
::v-deep .global-table th,
::v-deep .global-table td{
  border: 0.5px solid #000 !important;
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep .global-table thead th{
  position: sticky;
  top: 0;
  z-index: 10;
  background-clip: padding-box;
  transform: translateZ(0);
  will-change: transform;
  color: #000 !important;
  font-weight: 600;
}
::v-deep .global-table thead tr:first-child th{
  top: 0;
  height: var(--thead-row1-h);
  z-index: 12;
}
::v-deep .global-table thead tr:nth-child(2) th{
  top: calc(var(--thead-row1-h) + var(--th-border));
  z-index: 11;
}

::v-deep .global-table thead tr:first-child th:first-child{
  position: sticky;
  left: 0;
  top: 0;
  z-index: 32;
  width: var(--first-col-w);
  min-width: var(--first-col-w);
  max-width: var(--first-col-w);
}
::v-deep .global-table thead tr:nth-child(2) th:first-child{
  position: sticky;
  left: 0;
  top: calc(var(--thead-row1-h) + var(--th-border));
  z-index: 31;
  width: var(--first-col-w);
  min-width: var(--first-col-w);
  max-width: var(--first-col-w);
}

::v-deep .global-table tbody td:first-child{
  position: sticky;
  left: 0;
  z-index: 30;
  width: var(--first-col-w);
  min-width: var(--first-col-w);
  max-width: var(--first-col-w);
  box-shadow: 1px 0 0 rgba(0,0,0,.12);
  border-left-width: 1px !important;
}

::v-deep .global-table thead th[rowspan]{
  background-color: var(--first-col-bg) !important;
  color: var(--first-col-text) !important;
}
::v-deep .global-table thead th[rowspan]::before{
  content:"";
  position:absolute;
  inset:0;
  z-index:-1;
  background-color: var(--first-col-bg);
}

::v-deep .global-table thead tr:nth-child(2) th:first-child:not([class*="bg-"]) {
  background-color: var(--thead-row2-first-bg) !important;
  color: inherit !important;
}

::v-deep .global-table tbody td:first-child{
  background-color: var(--first-col-bg) !important;
  color: var(--first-col-text) !important;
}
::v-deep .global-table.table.is-striped tbody tr:nth-child(odd) td:first-child,
::v-deep .global-table.table.is-striped tbody tr:nth-child(even) td:first-child{
  background-color: var(--first-col-bg) !important;
}
::v-deep .global-table tbody tr:hover td:first-child{
  background-color: var(--first-col-bg) !important;
}

::v-deep .global-table td.comments-cell{
  white-space: pre-line;
  word-break: break-word;
}

::v-deep .global-table td.comments-cell,
::v-deep .global-table th.comments-cell {
  min-width: 200px;
  max-width: 350px;
  width: 250px;
}
</style>
